import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, type WaitlistEntry } from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { currentServiceDate, minutesSince } from '../lib/time.js';
import { ServiceDatePicker } from '../components/ServiceDatePicker.js';

/**
 * The waitlist.
 *
 * A waitlist entry is its own thing, not a reservation with the time left off.
 * It has no table, no time and no turn, so modelling it as a booking would put
 * phantom parties into every pacing count. Seating one CONVERTS it into a
 * walk-in reservation, which is what it actually becomes.
 *
 * The column that earns its place is the real wait against the quoted one. A
 * host who told someone "twenty minutes" forty minutes ago needs to see that
 * before the guest tells them.
 */
export function WaitlistScreen() {
  const { t } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [serviceDate, setServiceDate] = useState(() =>
    venue ? currentServiceDate(venue) : '',
  );
  const [partySize, setPartySize] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [quoted, setQuoted] = useState(20);
  const [notice, setNotice] = useState<string | null>(null);

  const date = serviceDate || (venue ? currentServiceDate(venue) : '');

  const list = useQuery({
    queryKey: ['waitlist', venueId, date],
    enabled: Boolean(venueId && date),
    queryFn: () =>
      api.get<{ entries: WaitlistEntry[] }>('/api/v1/waitlist', {
        venue_id: venueId,
        date,
      }),
    refetchInterval: 30_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    void queryClient.invalidateQueries({ queryKey: ['floor'] });
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.post('/api/v1/waitlist', {
        venue_id: venueId,
        service_date: date,
        party_size: partySize,
        ...(name ? { first_name: name } : {}),
        ...(phone ? { phone } : {}),
        quoted_wait_minutes: quoted,
      }),
    onSuccess: () => {
      setName('');
      setPhone('');
      invalidate();
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; status: string }) =>
      api.patch(`/api/v1/waitlist/${input.id}`, {
        venue_id: venueId,
        status: input.status,
      }),
    onSuccess: invalidate,
  });

  const seat = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/v1/waitlist/${id}/seat`, { venue_id: venueId }),
    onSuccess: () => {
      setNotice(null);
      invalidate();
    },
    onError: (err: unknown) =>
      setNotice(err instanceof ApiError ? err.message : t('errors.generic')),
  });

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const entries = list.data?.entries ?? [];
  const waiting = entries.filter((entry) => entry.status !== 'seated' && entry.status !== 'abandoned');

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{t('waitlist.title')}</h1>
        <ServiceDatePicker value={date} onChange={setServiceDate} venue={venue} />
      </header>

      {notice ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{notice}</p>
      ) : null}

      {can('waitlist:manage') ? (
        <form
          className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 bg-white p-3"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('common.guest')}</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('drawer.phone')}</span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              inputMode="tel"
              className="rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('common.partySize')}</span>
            <input
              type="number"
              min={1}
              max={50}
              value={partySize}
              onChange={(event) => setPartySize(Number(event.target.value) || 1)}
              className="w-20 rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="uppercase tracking-wide text-slate-500">{t('waitlist.quoted')}</span>
            <input
              type="number"
              min={0}
              max={600}
              step={5}
              value={quoted}
              onChange={(event) => setQuoted(Number(event.target.value) || 0)}
              className="w-20 rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={add.isPending}
            className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
          >
            {t('waitlist.add')}
          </button>
        </form>
      ) : null}

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {waiting.map((entry) => {
          const waited = minutesSince(entry.joined_at);
          // Past the quote is the number a host needs before the guest says it.
          const overQuote = entry.quoted_wait_minutes !== null && waited > entry.quoted_wait_minutes;

          return (
            <li key={entry.id} className="flex flex-wrap items-center gap-3 p-3">
              <span className="min-w-32 font-medium">
                {[entry.first_name, entry.last_name].filter(Boolean).join(' ') ||
                  t('common.guest')}
              </span>
              <span className="font-mono text-sm" data-numeric>
                {entry.party_size}
              </span>
              <span
                className={[
                  'rounded px-2 py-0.5 font-mono text-xs',
                  overQuote ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600',
                ].join(' ')}
                data-numeric
              >
                {`${waited} / ${entry.quoted_wait_minutes ?? '—'} ${t('waitlist.minutes')}`}
              </span>

              {can('waitlist:manage') ? (
                <div className="ms-auto flex gap-1">
                  {entry.status === 'waiting' ? (
                    <button
                      type="button"
                      onClick={() => update.mutate({ id: entry.id, status: 'notified' })}
                      className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-brand-teal-light"
                    >
                      {t('waitlist.notify')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => seat.mutate(entry.id)}
                    disabled={seat.isPending}
                    className="rounded bg-brand-teal px-2 py-1 text-xs text-white hover:bg-brand-teal-dark disabled:opacity-50"
                  >
                    {t('waitlist.seat')}
                  </button>
                  <button
                    type="button"
                    onClick={() => update.mutate({ id: entry.id, status: 'abandoned' })}
                    className="rounded border border-slate-200 px-2 py-1 text-xs hover:bg-slate-100"
                  >
                    {t('waitlist.abandon')}
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {waiting.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          {t('waitlist.empty')}
        </p>
      ) : null}
    </div>
  );
}
